import { cn } from '@lib/cn';
import { SelectField } from '@shared/ui';

import {
    CLIENT_ENVIRONMENTS,
    describeTarget,
    ENVIRONMENT_BLURB,
    ENVIRONMENT_LABEL,
} from '../schemas/target';

import type { ClientTargetState } from '../hooks/use-client-target';

/**
 * Which backend these screens are pointed at.
 *
 * Rendered at the top of every clients screen rather than in the app topbar:
 * the topbar is shared with Feature Flags, whose environment concept is a
 * different shape entirely, and a switcher up there would read as scoping that
 * too.
 */

const DOT: Record<string, string> = {
    dev: 'bg-blue-500',
    stg: 'bg-amber-500',
    prd: 'bg-red-500',
    eph: 'bg-gray-400',
};

export function EnvironmentScope({ state }: { state: ClientTargetState }) {
    const { target, slotDraft, isSlotDirty, isSlotMissing } = state;

    return (
        <div className="mb-5 flex flex-col gap-3">
            {target.env === 'prd' && <ProductionBand />}

            <div className="flex flex-wrap items-end gap-3">
                <div className="w-56">
                    <SelectField
                        label="Environment"
                        value={target.env}
                        onChange={state.setEnvironment}
                        options={CLIENT_ENVIRONMENTS.map((env) => ({
                            value: env,
                            label: ENVIRONMENT_LABEL[env],
                        }))}
                    />
                </div>

                {target.env === 'eph' && <SlotField state={state} />}

                <p className="pb-2 text-[12.5px] text-gray-500">{ENVIRONMENT_BLURB[target.env]}</p>
            </div>

            {isSlotDirty && <UnappliedSlot current={describeTarget(target)} next={slotDraft} />}
            {isSlotMissing && !isSlotDirty && <NoSlotChosen />}
        </div>
    );
}

/**
 * Always visible, never dismissible.
 *
 * Tinting the whole screen red stops being seen within ten minutes; a thin band
 * that is always in the same place keeps reading. Same reasoning the flags
 * module records for `prd` — the widest blast radius on the ladder.
 */
function ProductionBand() {
    return (
        <div className="flex items-center gap-2.5 rounded-lg bg-red-600 px-4 py-2 text-white">
            <span className="rounded-full border border-white/45 px-2 py-px text-[10.5px] font-semibold uppercase tracking-wide">
                Production
            </span>
            <span className="text-[12.5px] font-semibold">
                Everything on this screen is real customer data.
            </span>
        </div>
    );
}

/**
 * The slot, typed and then applied.
 *
 * `onChange` moves the draft only. The target changes on Enter or blur, which
 * is the moment the slot is chosen — wired to every keystroke, typing `dev1`
 * would query four targets, three of which do not exist.
 */
function SlotField({ state }: { state: ClientTargetState }) {
    return (
        <div className="flex flex-col gap-1.5">
            <label htmlFor="client-slot" className="text-[12.5px] font-semibold text-gray-700">
                Slot
            </label>
            <div className="flex items-center gap-2">
                <input
                    id="client-slot"
                    value={state.slotDraft}
                    placeholder="dev1"
                    autoComplete="off"
                    onChange={(event) => {
                        state.setSlotDraft(event.target.value);
                    }}
                    onBlur={state.applySlot}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            state.applySlot();
                        }
                        if (event.key === 'Escape') {
                            event.preventDefault();
                            state.discardSlotDraft();
                        }
                    }}
                    className={cn(
                        'w-28 rounded-lg border bg-white px-3 py-2 font-mono text-[13px] text-gray-900 outline-none transition-colors focus:border-brand focus:ring-2 focus:ring-brand-50',
                        state.isSlotDirty || state.isSlotMissing
                            ? 'border-brand'
                            : 'border-gray-200',
                    )}
                />
                {state.isSlotDirty && (
                    <span className="whitespace-nowrap text-[11px] font-semibold text-brand-700">
                        ↵ to switch
                    </span>
                )}
            </div>
        </div>
    );
}

/**
 * The typed slot and the one being shown disagree.
 *
 * Worth its own line because it is the trap this control exists to avoid: a box
 * reading `dev3` above a list that is still `dev1`.
 */
function UnappliedSlot({ current, next }: { current: string; next: string }) {
    return (
        <div className="flex items-center gap-2 rounded-lg border border-dashed border-brand-200 bg-brand-50 px-3.5 py-2.5 text-[12.5px] text-brand-700">
            <span>
                Still showing <b className="font-semibold">{current}</b>. Press Enter to switch to{' '}
                <b className="font-semibold">Ephemeral slot · {next || 'no slot'}</b>.
            </span>
        </div>
    );
}

function NoSlotChosen() {
    return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-[12.5px] text-gray-600">
            Choose a slot. Each one is its own deployment, with its own clients and its own Cognito
            pools — without one there is nothing to address.
        </div>
    );
}

export function EnvironmentDot({ env }: { env: string }) {
    return <span className={cn('size-2 shrink-0 rounded-full', DOT[env] ?? DOT.eph)} />;
}
